import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { assetsApi } from '../../lib/assetsApi';
import { useNotification } from '../../contexts/NotificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiCheck, FiX, FiLoader, FiRefreshCw, FiTestTube, FiFolderPlus, FiType } = FiIcons;

export default function UploadTester() {
  const [testResults, setTestResults] = useState([]);
  const [testing, setTesting] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { showSuccess, showError, showInfo } = useNotification();

  const runTests = async () => {
    setTesting(true);
    setTestResults([]);

    const tests = [
      {
        name: 'Health Check',
        test: async () => {
          const result = await assetsApi.healthCheck();
          return result.ok === true;
        }
      },
      {
        name: 'Initialize Storage',
        test: async () => {
          const result = await assetsApi.initializeStorage();
          return result.success === true;
        }
      },
      {
        name: 'List Assets',
        test: async () => {
          const result = await assetsApi.listAssets();
          return Array.isArray(result.assets);
        }
      },
      {
        name: 'File Upload (Mock)',
        test: async () => {
          // Create a mock file for testing
          const mockFile = new File(['test content'], 'test.txt', { type: 'text/plain' });
          try {
            const result = await assetsApi.uploadAsset(mockFile, 'test');
            return result.id && result.name;
          } catch (error) {
            console.error('Upload test failed:', error);
            return false;
          }
        }
      }
    ];

    for (const test of tests) {
      try {
        const passed = await test.test();
        setTestResults(prev => [...prev, { name: test.name, passed, error: null }]);
      } catch (error) {
        setTestResults(prev => [...prev, { name: test.name, passed: false, error: error.message }]);
      }
    }

    setTesting(false);
  };

  const initializeStorage = async () => {
    try {
      showInfo('Initializing Dropbox storage...');
      const result = await assetsApi.initializeStorage();
      if (result.success) {
        showSuccess('Storage initialized successfully');
      } else {
        showError('Failed to initialize storage: ' + result.error);
      }
    } catch (error) {
      showError('Storage initialization failed: ' + error.message);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadResults([]);
    
    try {
      const uploadPromises = files.map(async (file) => {
        const result = await assetsApi.uploadAsset(file, 'debug-test');
        return {
          name: file.name,
          size: file.size,
          result,
          status: 'success'
        };
      });
      
      const results = await Promise.allSettled(uploadPromises);
      
      const successfulUploads = results
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
        
      const failedUploads = results
        .filter(result => result.status === 'rejected')
        .map(result => ({ status: 'failed', error: result.reason.message }));
      
      setUploadResults(prev => [...prev, ...successfulUploads, ...failedUploads]);
      
      if (successfulUploads.length > 0) {
        showSuccess(`${successfulUploads.length} file(s) uploaded successfully`);
      }
      
      if (failedUploads.length > 0) {
        showError(`${failedUploads.length} file(s) failed to upload`);
      }
    } catch (error) {
      showError('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
    setUploadResults([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload API Tester</h1>
        <p className="text-gray-600">Test the upload functionality and API endpoints</p>
      </div>

      {/* API Tests Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">API Tests</h2>
          <div className="flex space-x-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={initializeStorage}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiFolderPlus} className="w-4 h-4 mr-2" />
              Initialize Storage
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={runTests}
              disabled={testing}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {testing ? (
                <SafeIcon icon={FiLoader} className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <SafeIcon icon={FiTestTube} className="w-4 h-4 mr-2" />
              )}
              {testing ? 'Running Tests...' : 'Run Tests'}
            </motion.button>
          </div>
        </div>

        {testResults.length > 0 && (
          <div className="space-y-3">
            {testResults.map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                  result.passed 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center">
                  <SafeIcon 
                    icon={result.passed ? FiCheck : FiX} 
                    className={`w-5 h-5 mr-3 ${
                      result.passed ? 'text-green-600' : 'text-red-600'
                    }`} 
                  />
                  <div>
                    <p className="font-medium text-gray-900">{result.name}</p>
                    {result.error && (
                      <p className="text-sm text-red-600">{result.error}</p>
                    )}
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  result.passed 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {result.passed ? 'PASS' : 'FAIL'}
                </span>
              </motion.div>
            ))}
          </div>
        )}

        {testResults.length === 0 && !testing && (
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiTestTube} className="w-12 h-12 mx-auto mb-4" />
            <p>Click "Initialize Storage" first, then "Run Tests" to start API testing</p>
          </div>
        )}
      </div>

      {/* File Upload Test Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="text-center mb-6">
          <SafeIcon icon={FiUpload} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test File Uploads</h2>
          <input 
            type="file" 
            multiple 
            onChange={handleFileUpload} 
            disabled={uploading}
            className="hidden" 
            id="debug-upload"
          />
          <label 
            htmlFor="debug-upload"
            className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer ${
              uploading 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {uploading ? (
              <>
                <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                Choose Files to Test Upload
              </>
            )}
          </label>
        </div>

        {uploadResults.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload Results ({uploadResults.length})</h3>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={clearResults}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Results
              </motion.button>
            </div>
            
            <div className="space-y-3">
              {uploadResults.map((file, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                    file.status === 'success' 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center">
                    <SafeIcon 
                      icon={file.status === 'success' ? FiCheck : FiX} 
                      className={`w-5 h-5 mr-3 ${
                        file.status === 'success' ? 'text-green-600' : 'text-red-600'
                      }`} 
                    />
                    <div>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      {file.size && (
                        <p className="text-sm text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      )}
                      {file.error && (
                        <p className="text-sm text-red-600">{file.error}</p>
                      )}
                      {file.result?.dropbox_path && (
                        <p className="text-sm text-gray-500">
                          Path: {file.result.dropbox_path}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    file.status === 'success' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {file.status === 'success' ? 'SUCCESS' : 'FAILED'}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Environment Info */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Environment Info</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Runtime:</span>
            <span className="ml-2 text-gray-600">Browser</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">API Base:</span>
            <span className="ml-2 text-gray-600">/api</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Storage:</span>
            <span className="ml-2 text-gray-600">Dropbox</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Database:</span>
            <span className="ml-2 text-gray-600">NCB</span>
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Make sure to set VITE_DROPBOX_ACCESS_TOKEN in your .env file for direct Dropbox access from the browser.
          </p>
        </div>
      </div>
    </div>
  );
}